import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HexLogo } from "../components/HexLogo";
import { HexGrid } from "../components/HexGrid";
import { NoticiasDestacadasHome } from "../components/NoticiasDestacadasHome";
import { PartnersStrip } from "../components/PartnersStrip";
import { ProgramCard } from "../components/ProgramCard";
import { programs as staticPrograms } from "../data/programs";
import { useProgramas } from "../hooks/useCms";
import { useSiteConfig } from "../hooks/useSiteConfig";
import { programaCmsToCard } from "../lib/adapters";

const ageGroups = [
  { name: "Descubridores", grade: "1º y 2º de Primaria", color: "bg-accent-leaf" },
  { name: "Soñadores", grade: "3º y 4º de Primaria", color: "bg-accent-sun" },
  { name: "Navegantes", grade: "5º y 6º de Primaria", color: "bg-accent-cobalt" },
];

const pillars = [
  {
    title: "Profesores universitarios",
    body: "Investigadores, ingenieros y especialistas en educación con vocación por la ciencia y la tecnología.",
  },
  {
    title: "Metodología innovadora",
    body: "Programación, robots, talleres prácticos. Cada día se convierten en un profesional científico distinto.",
  },
  {
    title: "Programa educativo",
    body: "Diseñado para desarrollar la creatividad, el pensamiento lógico y la resolución de problemas.",
  },
];

export function HomePage() {
  // CMS first, fallback al dataset estático si todavía no hay contenido sembrado.
  const { data: cmsProgramas } = useProgramas();
  const site = useSiteConfig();

  const programs =
    cmsProgramas && cmsProgramas.length > 0
      ? cmsProgramas.map(programaCmsToCard)
      : staticPrograms;

  return (
    <>
      <Helmet>
        <title>Campus EMJC — {site.tagline}</title>
        <meta
          name="description"
          content="Campus de la Escuela Municipal de Jóvenes Científicos: campamentos, talleres y ferias para niños de 8 a 12 años en Extremadura."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            name: "Escuela Municipal de Jóvenes Científicos",
            alternateName: "Campus EMJC",
            url: "https://campusemjc.unex.es/",
            logo: "https://campusemjc.unex.es/logo.svg",
            description: site.description,
            email: site.email,
            parentOrganization: {
              "@type": "CollegeOrUniversity",
              name: "Universidad de Extremadura",
              url: "https://www.unex.es/",
            },
            areaServed: {
              "@type": "AdministrativeArea",
              name: "Extremadura",
            },
            audience: {
              "@type": "EducationalAudience",
              educationalRole: "student",
              audienceType: "Niños y niñas de 8 a 12 años",
            },
            sameAs: [site.social.facebook.url, site.social.instagram.url],
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden pb-20 pt-24 text-brand-ink">
        <HexGrid />
        <div className="container-emjc relative">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="badge"
          >
            {site.university} · {site.ages}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 max-w-4xl font-display text-5xl font-extrabold leading-[1.05] tracking-tight md:text-7xl lg:text-[88px]"
          >
            <span className="inline-flex align-middle">
              <HexLogo size={84} className="mr-3 text-brand-ink" />
            </span>
            Cada día <em className="font-semibold not-italic text-brand-orange">un científico</em>{" "}
            diferente.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-7 max-w-2xl text-xl text-gray-700"
          >
            {site.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/programas" className="btn-primary">
              Conoce los programas
            </Link>
            <a href={`mailto:${site.email}`} className="btn-ghost">
              Solicita información
            </a>
          </motion.div>

          <p className="mt-10 inline-block -rotate-2 font-hand text-3xl text-brand-orange">
            — Una iniciativa de la EMJC y la UEx —
          </p>
        </div>
      </section>

      {/* Pilares */}
      <section className="py-20">
        <div className="container-emjc grid gap-10 md:grid-cols-3">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="rounded-2xl bg-white p-8 ring-1 ring-gray-300"
            >
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                <span className="font-display text-lg font-bold">{i + 1}</span>
              </div>
              <h2 className="font-display text-2xl font-semibold">{p.title}</h2>
              <p className="mt-3 text-gray-700">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Programas */}
      <section className="py-20">
        <div className="container-emjc">
          <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange-deep">
                Nuestros programas
              </p>
              <h2 className="mt-2 font-display text-4xl font-semibold md:text-5xl">
                Cuatro caminos para descubrir la ciencia.
              </h2>
            </div>
            <Link to="/programas" className="text-sm font-semibold text-brand-ink hover:text-brand-orange">
              Ver todos los programas →
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {programs.map((program) => (
              <ProgramCard key={program.slug} program={program} />
            ))}
          </div>
        </div>
      </section>

      {/* Noticias destacadas (sólo se renderiza si hay alguna con
          destacada=true en el CMS) */}
      <NoticiasDestacadasHome />

      {/* Categorías edad */}
      <section className="bg-white py-20">
        <div className="container-emjc">
          <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange-deep">
            Programa educativo
          </p>
          <h2 className="mt-2 max-w-2xl font-display text-4xl font-semibold md:text-5xl">
            Tres etapas, un mismo panal del descubrimiento.
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {ageGroups.map((g) => (
              <div
                key={g.name}
                className="relative overflow-hidden rounded-3xl border border-gray-300 p-8"
              >
                <div className={`hex absolute -right-10 -top-10 h-32 w-32 ${g.color} opacity-80`} />
                <h3 className="relative font-display text-2xl font-semibold">{g.name}</h3>
                <p className="relative mt-2 text-gray-700">{g.grade}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Patrocinadores y colaboradores */}
      <PartnersStrip />

      {/* CTA final */}
      <section className="py-24">
        <div className="container-emjc">
          <div className="relative overflow-hidden rounded-3xl bg-brand-ink px-8 py-16 text-brand-paper md:px-16 md:py-24">
            <HexGrid opacity={0.1} className="text-brand-paper" />
            <div className="relative max-w-2xl">
              <h2 className="font-display text-4xl font-semibold leading-tight md:text-5xl">
                ¿Y si te despertaras siendo arqueólogo?{" "}
                <span className="text-brand-orange">¿Y mañana, robotista?</span>
              </h2>
              <p className="mt-5 text-lg text-gray-300">
                Solicita información sobre matrícula y turnos para el próximo curso.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a href={`mailto:${site.email}`} className="btn-primary">
                  Solicita información
                </a>
                <Link to="/programas" className="btn border-2 border-brand-paper text-brand-paper hover:bg-brand-paper hover:text-brand-ink">
                  Ver programas
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
